(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const scrollToPricing = (event) => {
    const link = event.target.closest('[data-papelia-scroll]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href') || '#papelia-pack-selector');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
  };

  const purchase = async (event) => {
    const form = event.target.closest('[data-papelia-product-form]');
    if (!form || event.defaultPrevented) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-papelia-form-status]');
    if (!button || button.disabled || button.dataset.loading === 'true') return;
    button.dataset.loading = 'true';
    button.disabled = true;
    status.textContent = '';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(window.Theme?.routes?.cart_add_url || `${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'No fue posible añadir este pack.');
      window.location.assign(form.dataset.purchaseMode === 'checkout' ? `${root}checkout` : window.Theme?.routes?.cart_url || `${root}cart`);
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'Ocurrió un error. Inténtalo de nuevo.';
      button.dataset.loading = 'false';
      button.disabled = false;
    }
  };

  const setFaqState = (button, open, animate = true) => {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    button.setAttribute('aria-expanded', String(open));
    if (open) {
      panel.hidden = false;
      const endHeight = panel.scrollHeight;
      if (!animate || reducedMotion.matches) {
        panel.style.height = 'auto';
        return;
      }
      panel.style.height = '0px';
      requestAnimationFrame(() => {
        panel.style.height = `${endHeight}px`;
      });
      panel.addEventListener('transitionend', () => {
        if (button.getAttribute('aria-expanded') === 'true') panel.style.height = 'auto';
      }, { once: true });
      return;
    }
    if (!animate || reducedMotion.matches) {
      panel.style.height = '0px';
      panel.hidden = true;
      return;
    }
    panel.style.height = `${panel.scrollHeight}px`;
    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });
    panel.addEventListener('transitionend', () => {
      if (button.getAttribute('aria-expanded') === 'false') panel.hidden = true;
    }, { once: true });
  };

  const initFaq = (root) => {
    root.querySelectorAll('[data-papelia-faq]').forEach((faq) => {
      if (faq.dataset.initialized) return;
      faq.dataset.initialized = 'true';
      faq.querySelectorAll('.papelia-faq__trigger').forEach((button) => {
        setFaqState(button, button.getAttribute('aria-expanded') === 'true', false);
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') !== 'true';
          if (open && faq.dataset.allowMultiple !== 'true') {
            faq.querySelectorAll('.papelia-faq__trigger[aria-expanded="true"]').forEach((other) => {
              if (other !== button) setFaqState(other, false);
            });
          }
          setFaqState(button, open);
        });
      });
    });
  };

  const initGalleryTrack = (shell) => {
    if (shell.dataset.initialized) return;
    shell.dataset.initialized = 'true';
    const viewport = shell.querySelector('.papelia-gallery__viewport');
    const previous = shell.querySelector('[data-gallery-prev]');
    const next = shell.querySelector('[data-gallery-next]');
    if (!viewport) return;
    let paused = false;
    let lastTime = 0;
    let manualPauseUntil = 0;
    const autoplay = shell.dataset.autoplay === 'true';
    const direction = shell.dataset.direction === 'reverse' ? -1 : 1;
    const speed = Math.max(8, Number(shell.dataset.speed) || 24);

    const move = (amount) => {
      manualPauseUntil = Date.now() + 1400;
      viewport.scrollBy({ left: amount, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    previous?.addEventListener('click', () => move(-viewport.clientWidth * 0.78));
    next?.addEventListener('click', () => move(viewport.clientWidth * 0.78));
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-viewport.clientWidth * 0.78);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(viewport.clientWidth * 0.78);
      }
    });
    shell.addEventListener('mouseenter', () => { paused = true; });
    shell.addEventListener('mouseleave', () => { paused = false; });
    shell.addEventListener('focusin', () => { paused = true; });
    shell.addEventListener('focusout', () => { paused = false; });
    viewport.addEventListener('pointerdown', () => { paused = true; });
    viewport.addEventListener('pointerup', () => { paused = false; });

    if (direction < 0) requestAnimationFrame(() => { viewport.scrollLeft = viewport.scrollWidth; });
    const tick = (time) => {
      if (autoplay && !paused && Date.now() >= manualPauseUntil && !document.hidden && !reducedMotion.matches) {
        const elapsed = lastTime ? (time - lastTime) / 1000 : 0;
        const max = viewport.scrollWidth - viewport.clientWidth;
        viewport.scrollLeft += direction * speed * elapsed;
        if (direction > 0 && viewport.scrollLeft >= max - 1) viewport.scrollLeft = 0;
        if (direction < 0 && viewport.scrollLeft <= 0) viewport.scrollLeft = max;
      }
      lastTime = time;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const initReveal = (root) => {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      root.querySelectorAll('[data-papelia-reveal]').forEach((item) => item.classList.add('papelia-is-visible'));
      return;
    }
    document.documentElement.classList.add('papelia-motion-ready');
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('papelia-is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    root.querySelectorAll('[data-papelia-reveal]:not(.papelia-is-visible)').forEach((item) => observer.observe(item));
  };

  const initSticky = (root) => {
    root.querySelectorAll('[data-papelia-sticky]').forEach((bar) => {
      if (bar.dataset.initialized) return;
      bar.dataset.initialized = 'true';
      const hero = document.querySelector('.papelia-hero');
      if (!hero || !('IntersectionObserver' in window)) {
        bar.hidden = false;
        return;
      }
      const observer = new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting;
      }, { threshold: 0.12 });
      observer.observe(hero);
    });
  };

  const init = (root = document) => {
    initFaq(root);
    root.querySelectorAll('[data-papelia-gallery-track]').forEach(initGalleryTrack);
    initReveal(root);
    initSticky(root);
  };

  document.addEventListener('click', scrollToPricing);
  document.addEventListener('submit', purchase);
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
